import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  type Row,
  type SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, ChevronDown, ChevronRight, Link2, Loader2, Upload } from 'lucide-react';
import type { QueueGroupingField, QueueRow } from '@/api/cockpit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { QueueDeleteDialog } from '@/components/common/QueueDeleteDialog';
import { Card, CardContent } from '@/components/ui/card';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useQueue, useQueueViewConfig } from '@/features/exceptions/hooks';
import { useInboundUpload } from '@/features/orders/hooks';
import { useHasRole } from '@/auth/AuthProvider';
import {
  cn,
  formatDateTime,
  formatQueueGroupLabel,
  formatRelativeTime,
  formatWorkflowStateLabel,
  getQueueGroupValue,
  getQueueItemReference,
  getQueueReceivedOn,
} from '@/lib/utils';

const columnHelper = createColumnHelper<QueueRow>();

const DEFAULT_QUEUE_VIEW_CONFIG = {
  groupingEnabled: false,
  groupingField: 'po' as QueueGroupingField,
  groupsDefaultExpanded: true,
};

function formatCurrency(value: number | null): string {
  if (value === null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
}

function SortableHeader({ label, onClick }: { label: string; onClick: () => void }): JSX.Element {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="-ml-2 min-h-8 max-w-full px-2 py-1 text-left whitespace-normal"
      onClick={onClick}
      title={`Sort by ${label}`}
    >
      <span className="whitespace-normal leading-tight">{label}</span>
      <ArrowUpDown className="h-4 w-4 shrink-0" />
    </Button>
  );
}

const columns = [
  columnHelper.accessor('state', {
    header: ({ column }) => (
      <SortableHeader
        label="State"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      />
    ),
    cell: ({ row, getValue }) => {
      const state = getValue();
      const isApprovalState = state === 'Awaiting Operator Approval';
      return (
        <div className="flex min-w-0 justify-center">
          <Badge
            variant={getStateBadgeVariant(state)}
            className="inline-flex max-w-full min-w-0 items-center gap-1.5 overflow-hidden whitespace-nowrap"
            title={formatWorkflowStateLabel(state)}
          >
            <span className="min-w-0 truncate">{formatWorkflowStateLabel(state)}</span>
            {isApprovalState ? (
              <Link
                to={`/drafts?draft=${encodeURIComponent(row.original.id)}`}
                className="inline-flex shrink-0 rounded-sm p-0.5 text-current transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title="Open draft"
                aria-label="Open draft"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                <Link2 className="h-3.5 w-3.5" />
              </Link>
            ) : null}
          </Badge>
        </div>
      );
    },
  }),
  columnHelper.accessor('customerName', {
    header: ({ column }) => (
      <SortableHeader
        label="Customer"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      />
    ),
    cell: ({ row, getValue }) => {
      const customer = getValue();
      const customerLabel = row.original.customerNumber
        ? `${customer} ${row.original.customerNumber}`
        : customer;

      return (
        <div className="truncate font-medium" title={customerLabel}>
          {customerLabel}
        </div>
      );
    },
  }),
  columnHelper.accessor('poNumber', {
    header: ({ column }) => (
      <SortableHeader
        label="PO"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      />
    ),
    cell: ({ getValue }) => (
      <span className="block truncate" title={getValue()}>
        {getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('salesOrderNumber', {
    header: ({ column }) => (
      <SortableHeader
        label="Sales Id"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      />
    ),
    cell: ({ getValue }) => (
      <span
        className="block truncate text-sm text-muted-foreground"
        title={getValue() ?? 'Pending draft'}
      >
        {getValue() ?? 'Pending draft'}
      </span>
    ),
  }),
  columnHelper.accessor('productCode', {
    header: ({ column }) => (
      <SortableHeader
        label="Product Code"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      />
    ),
    cell: ({ getValue }) => (
      <span
        className="block truncate text-sm text-muted-foreground"
        title={getValue() ?? 'Pending match'}
      >
        {getValue() ?? 'Pending match'}
      </span>
    ),
  }),
  columnHelper.accessor('requestedShippingDate', {
    header: ({ column }) => (
      <SortableHeader
        label="Requested Ship Date"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      />
    ),
    cell: ({ getValue }) => (
      <span
        className="block truncate text-sm text-muted-foreground"
        title={getValue() ?? 'Pending extraction'}
      >
        {getValue() ?? 'Pending extraction'}
      </span>
    ),
  }),
  columnHelper.accessor('totalOrderValue', {
    header: ({ column }) => (
      <SortableHeader
        label="Total Order Value"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      />
    ),
    cell: ({ getValue }) => {
      const formattedValue = formatCurrency(getValue());

      return (
        <span className="block truncate text-sm text-muted-foreground" title={formattedValue}>
          {formattedValue}
        </span>
      );
    },
  }),
  columnHelper.accessor('emailSubject', {
    id: 'emailSubject',
    header: ({ column }) => (
      <SortableHeader
        label="Subject"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      />
    ),
    sortingFn: (left, right, columnId) => {
      const a = (left.getValue<string | null>(columnId) ?? '').toLowerCase();
      const b = (right.getValue<string | null>(columnId) ?? '').toLowerCase();
      // Empty subjects sort to the bottom in either direction.
      if (a === b) return 0;
      if (a.length === 0) return 1;
      if (b.length === 0) return -1;
      return a.localeCompare(b);
    },
    cell: ({ getValue }) => {
      const value = getValue();
      const display = value && value.trim().length > 0 ? value : '-';
      return (
        <span
          className="block truncate text-sm text-muted-foreground"
          title={display}
        >
          {display}
        </span>
      );
    },
  }),
  columnHelper.accessor('emailSender', {
    id: 'emailSender',
    header: ({ column }) => (
      <SortableHeader
        label="From"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      />
    ),
    sortingFn: (left, right, columnId) => {
      const a = (left.getValue<string | null>(columnId) ?? '').toLowerCase();
      const b = (right.getValue<string | null>(columnId) ?? '').toLowerCase();
      if (a === b) return 0;
      if (a.length === 0) return 1;
      if (b.length === 0) return -1;
      return a.localeCompare(b);
    },
    cell: ({ getValue }) => {
      const value = getValue();
      if (!value || value.trim().length === 0) {
        return <span className="block truncate text-sm text-muted-foreground">{''}</span>;
      }
      return (
        <span className="block truncate text-sm text-muted-foreground" title={value}>
          {value}
        </span>
      );
    },
  }),
  columnHelper.accessor((row) => row.owner?.email ?? '', {
    id: 'owner',
    header: ({ column }) => (
      <SortableHeader
        label="Owner"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      />
    ),
    sortingFn: (left, right, columnId) => {
      const a = (left.getValue<string>(columnId) ?? '').toLowerCase();
      const b = (right.getValue<string>(columnId) ?? '').toLowerCase();
      if (a === b) return 0;
      if (a.length === 0) return 1;
      if (b.length === 0) return -1;
      return a.localeCompare(b);
    },
    cell: ({ row }) => {
      const owner = row.original.owner;
      if (!owner) {
        return <span className="block truncate text-sm text-muted-foreground">{''}</span>;
      }
      return (
        <span className="block truncate text-sm" title={`${owner.email} (source: ${owner.source})`}>
          {owner.email}
          {owner.source !== 'd365' && (
            <span className="ml-1 text-xs text-muted-foreground">(email)</span>
          )}
        </span>
      );
    },
  }),
  columnHelper.accessor((row) => getQueueReceivedOn(row), {
    id: 'receivedOn',
    header: ({ column }) => (
      <SortableHeader
        label="Received on"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      />
    ),
    sortingFn: (left, right, columnId) => {
      const leftTime = new Date(left.getValue<string>(columnId)).getTime();
      const rightTime = new Date(right.getValue<string>(columnId)).getTime();
      if (Number.isNaN(leftTime) && Number.isNaN(rightTime)) return 0;
      if (Number.isNaN(leftTime)) return 1;
      if (Number.isNaN(rightTime)) return -1;
      return leftTime - rightTime;
    },
    cell: ({ row, getValue }) => {
      const receivedOn = getValue();
      const formattedReceivedOn = formatDateTime(receivedOn);
      const relativeReceivedOn = formatRelativeTime(receivedOn);
      const source =
        row.original.emailReceivedAt && row.original.emailReceivedAt.trim().length > 0
          ? 'customer email received date'
          : 'queue received date';

      return (
        <time
          className="block whitespace-normal text-xs leading-5 text-muted-foreground"
          dateTime={receivedOn}
          title={`${formattedReceivedOn} (${relativeReceivedOn}; ${source})`}
        >
          {formattedReceivedOn}
        </time>
      );
    },
  }),
  columnHelper.accessor('id', {
    header: 'Source document',
    cell: ({ row, table }) => {
      const reference = getQueueItemReference(row.original);
      const activeRowIds =
        (table.options.meta as { activeRowIds?: ReadonlySet<string> } | undefined)?.activeRowIds ??
        new Set<string>();
      const isProcessing =
        activeRowIds.has(row.original.id) || isQueueRowExecuting(row.original.state);

      return (
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            {isProcessing ? (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
            ) : null}
            <span className="min-w-0 truncate font-medium text-foreground" title={reference}>
              {reference}
            </span>
          </div>
        </div>
      );
    },
  }),
];

type RowStatusTone = 'neutral' | 'success' | 'warning' | 'danger';
type StateBadgeVariant = 'outline' | 'success' | 'destructive';

interface QueueGroup {
  key: string;
  label: string;
  field: QueueGroupingField;
  rows: Row<QueueRow>[];
  exceptionCount: number;
  orderCount: number;
  newestReceivedOn: string;
}

function getStateBadgeVariant(state: string): StateBadgeVariant {
  if (state === 'Exception - Awaiting Operator') return 'destructive';
  return state === 'Awaiting Operator Approval' ? 'success' : 'outline';
}

function getColumnClassName(columnId: string): string {
  switch (columnId) {
    case 'id':
      return 'w-[28%] min-w-0 sm:w-[24%] md:w-[20%] lg:w-[20%] xl:w-[14%] 2xl:w-[11%]';
    case 'state':
      return 'w-[18%] whitespace-nowrap lg:w-[14%] xl:w-[12%]';
    case 'customerName':
      return 'hidden md:table-cell md:w-[20%] lg:w-[16%] xl:w-[15%]';
    case 'poNumber':
      return 'hidden sm:table-cell sm:w-[13%] md:w-[11%] lg:w-[8%] xl:w-[7%]';
    case 'salesOrderNumber':
      return 'hidden lg:table-cell lg:w-[10%] xl:w-[9%]';
    case 'productCode':
      return 'hidden xl:table-cell xl:w-[7%]';
    case 'requestedShippingDate':
      return 'hidden lg:table-cell lg:w-[12%] xl:w-[9%]';
    case 'totalOrderValue':
      return 'hidden xl:table-cell xl:w-[9%]';
    case 'emailSubject':
      return 'hidden 2xl:table-cell 2xl:w-[12%]';
    case 'emailSender':
      return 'hidden min-[1700px]:table-cell min-[1700px]:w-[10%]';
    case 'owner':
      return 'hidden lg:table-cell lg:w-[9%]';
    case 'receivedOn':
      return 'w-[24%] whitespace-normal sm:w-[18%] md:w-[14%] lg:w-[10%] xl:w-[8%]';
    default:
      return '';
  }
}

function getRowStatusTone(row: QueueRow): RowStatusTone {
  const normalizedState = row.state.toLowerCase();
  const isException =
    row.exception ||
    normalizedState.includes('exception') ||
    normalizedState === 'worker error' ||
    normalizedState === 'retries exhausted';
  if (isException) return 'danger';

  const needsOperatorAttention =
    normalizedState.includes('awaiting operator') ||
    normalizedState.includes('under review') ||
    row.pipelineStageKey === 'approval';
  if (needsOperatorAttention) return 'warning';

  const isFinalized =
    row.pipelineStageKey === 'resolved' ||
    row.closeReason !== null ||
    ['done', 'closed', 'acknowledgement sent'].includes(normalizedState);
  if (isFinalized) return 'success';

  return 'neutral';
}

function isQueueRowExecuting(state: string): boolean {
  return EXECUTING_QUEUE_STATES.has(state);
}

// After commit 3c collapsed transient claim states, the only states that
// represent active execution in the queue are the operator-driven re-review
// and reprocessing states. Worker-in-flight on a waiting row is signalled
// by `claimed_by`, not by a distinct status.
const EXECUTING_QUEUE_STATES = new Set(['Exception - Under Review', 'Reprocessing']);

function getRowStatusStripClassName(tone: RowStatusTone): string {
  switch (tone) {
    case 'danger':
      return 'bg-destructive';
    case 'warning':
      return 'bg-warning';
    case 'success':
      return 'bg-emerald-500';
    case 'neutral':
      return 'bg-muted-foreground/35';
  }
}

function getOperatorCommunication(row: QueueRow): readonly string[] {
  const messages = row.warnings.filter(
    (warning) => warning !== 'No active warnings' && !isDetailOnlyWarning(warning),
  );
  if (messages.length > 0) return messages;
  if (row.closeReason) return [`Closed reason: ${row.closeReason}.`];
  if (row.exception) {
    return [
      'Review the extracted and matched fields, correct unresolved values, then save and resume the row.',
    ];
  }
  if (row.state === 'Awaiting Operator Approval') {
    return ['Review the draft sales order, then approve or reject it.'];
  }
  return [];
}

function isDetailOnlyWarning(message: string): boolean {
  return message.toLowerCase().includes('dynamics 365 sales price agreement');
}

function RowStatusStrip({ row }: { row: QueueRow }): JSX.Element {
  const tone = getRowStatusTone(row);
  const messages = tone === 'warning' || tone === 'danger' ? getOperatorCommunication(row) : [];
  const hasMessages = messages.length > 0;
  const strip = (
    <button
      type="button"
      className={cn(
        'relative h-full w-5 cursor-default bg-transparent focus-visible:outline-none',
        hasMessages && 'cursor-help',
      )}
      tabIndex={hasMessages ? 0 : -1}
      aria-label={
        hasMessages
          ? `${formatWorkflowStateLabel(row.state)}: ${messages.join(' ')}`
          : formatWorkflowStateLabel(row.state)
      }
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <span
        className={cn(
          'absolute inset-y-0 left-0 w-1 transition-[width] duration-150 group-hover/status:w-2 group-focus-within/status:w-2',
          getRowStatusStripClassName(tone),
        )}
        aria-hidden="true"
      />
    </button>
  );

  if (!hasMessages) {
    return <div className="absolute inset-y-0 left-0 z-10 flex items-stretch">{strip}</div>;
  }

  return (
    <div className="group/status absolute inset-y-0 left-0 z-10 flex items-stretch">
      <HoverCard openDelay={80} closeDelay={80}>
        <HoverCardTrigger asChild>{strip}</HoverCardTrigger>
        <HoverCardContent
          side="right"
          align="center"
          sideOffset={8}
          className="w-[28rem] max-w-[calc(100vw-4rem)] p-3"
        >
          <div className="font-medium">{formatWorkflowStateLabel(row.state)}</div>
          <div className="mt-1 space-y-1 text-muted-foreground">
            {messages.map((message) => (
              <p key={message} className="line-clamp-3">
                {message}
              </p>
            ))}
          </div>
        </HoverCardContent>
      </HoverCard>
    </div>
  );
}

function getQueueGroupingFieldLabel(field: QueueGroupingField): string {
  switch (field) {
    case 'email':
      return 'Email';
    case 'customer':
      return 'Customer';
    case 'salesOrder':
      return 'Sales order';
    case 'po':
      return 'PO';
  }
}

function buildQueueGroups(
  rows: Row<QueueRow>[],
  groupingField: QueueGroupingField,
): QueueGroup[] {
  const groups = new Map<string, QueueGroup>();

  for (const row of rows) {
    const original = row.original;
    const value = getQueueGroupValue(original, groupingField);
    const key = `${groupingField}:${value}`;
    const receivedOn = getQueueReceivedOn(original);
    const existing = groups.get(key);
    if (existing) {
      existing.rows.push(row);
      existing.orderCount += 1;
      if (original.exception) existing.exceptionCount += 1;
      if (Date.parse(receivedOn) > Date.parse(existing.newestReceivedOn)) {
        existing.newestReceivedOn = receivedOn;
      }
      continue;
    }

    groups.set(key, {
      key,
      label: formatQueueGroupLabel(original, groupingField),
      field: groupingField,
      rows: [row],
      exceptionCount: original.exception ? 1 : 0,
      orderCount: 1,
      newestReceivedOn: receivedOn,
    });
  }

  return Array.from(groups.values());
}

export function ExceptionsListPage(): JSX.Element {
  const [includeResolved, setIncludeResolved] = useState(false);
  const { data, isLoading, isError, error, refetch, isFetching } = useQueue({ includeResolved });
  const { data: queueViewConfig } = useQueueViewConfig();
  const effectiveQueueViewConfig = queueViewConfig ?? DEFAULT_QUEUE_VIEW_CONFIG;
  const upload = useInboundUpload();
  const navigate = useNavigate();
  const canDelete = useHasRole('SO-Admin');
  const [uploadMessage, setUploadMessage] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [stageFilter, setStageFilter] = useState<string>('all');
  // FFS-130 #744: filter queue rows by the resolved owner email. The drop-
  // down is populated from the distinct owners present in the current
  // dataset; rows with no resolved owner are excluded when a specific
  // filter is active. "all" passes everything through.
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'receivedOn', desc: true }]);
  const [activeRowIds, setActiveRowIds] = useState<Set<string>>(() => new Set());
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const tableData = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return (data ? [...data] : []).filter((row) => {
      if (stateFilter !== 'all' && row.state !== stateFilter) return false;
      if (stageFilter !== 'all' && row.pipelineStageKey !== stageFilter) return false;
      if (ownerFilter !== 'all') {
        if (ownerFilter === '__unassigned__') {
          if (row.owner) return false;
        } else if (row.owner?.email !== ownerFilter) {
          return false;
        }
      }
      if (normalizedSearch.length === 0) return true;
      const haystack = [
        getQueueItemReference(row),
        row.emailSubject,
        row.emailSender,
        row.owner?.email ?? null,
        row.state,
        row.customerName,
        row.customerNumber,
        row.poNumber,
        row.productCode,
        row.requestedDeliveryDate,
        row.requestedShippingDate,
        row.salesOrderNumber,
        getQueueReceivedOn(row),
      ]
        .filter((value): value is string => typeof value === 'string' && value.length > 0)
        .join(' ')
        .toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [data, search, stageFilter, stateFilter, ownerFilter]);
  const stateOptions = useMemo(
    () =>
      Array.from(new Set((data ?? []).map((row) => row.state))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [data],
  );
  const stageOptions = useMemo(
    () =>
      Array.from(new Set((data ?? []).map((row) => row.pipelineStageKey))).sort((left, right) =>
        left.localeCompare(right),
      ),
    [data],
  );
  const ownerOptions = useMemo(() => {
    const emails = new Set<string>();
    let hasUnassigned = false;
    for (const row of data ?? []) {
      if (row.owner?.email) emails.add(row.owner.email);
      else hasUnassigned = true;
    }
    return {
      emails: Array.from(emails).sort((left, right) => left.localeCompare(right)),
      hasUnassigned,
    };
  }, [data]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: { activeRowIds },
  });
  const tableRows = table.getRowModel().rows;
  const queueGroups = useMemo(
    () =>
      effectiveQueueViewConfig.groupingEnabled
        ? buildQueueGroups(tableRows, effectiveQueueViewConfig.groupingField)
        : [],
    [
      tableRows,
      effectiveQueueViewConfig.groupingEnabled,
      effectiveQueueViewConfig.groupingField,
    ],
  );
  const groupKeysSignature = queueGroups.map((group) => group.key).join('\n');
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setCollapsedGroupKeys(
      effectiveQueueViewConfig.groupsDefaultExpanded
        ? new Set()
        : new Set(queueGroups.map((group) => group.key)),
    );
  }, [
    effectiveQueueViewConfig.groupingField,
    effectiveQueueViewConfig.groupsDefaultExpanded,
    groupKeysSignature,
  ]);

  const visibleColumnCount = table.getVisibleLeafColumns().length;

  useEffect(() => {
    function onStepActivity(event: WindowEventMap['cockpit:step-activity']): void {
      const detail = event.detail;
      setActiveRowIds((current) => {
        const next = new Set(current);
        if (detail.phase === 'started') {
          next.add(detail.stagingRowId);
          return next;
        }
        next.delete(detail.stagingRowId);
        return next;
      });
    }

    function onTransition(event: WindowEventMap['cockpit:transition']): void {
      const detail = event.detail;
      if (!isQueueRowExecuting(detail.toState)) {
        setActiveRowIds((current) => {
          if (!current.has(detail.stagingRowId)) return current;
          const next = new Set(current);
          next.delete(detail.stagingRowId);
          return next;
        });
      }
    }

    window.addEventListener('cockpit:step-activity', onStepActivity);
    window.addEventListener('cockpit:transition', onTransition);
    return () => {
      window.removeEventListener('cockpit:step-activity', onStepActivity);
      window.removeEventListener('cockpit:transition', onTransition);
    };
  }, []);

  return (
    <div className="space-y-6 py-6">
      <div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Queue</h1>
            <p className="text-sm text-muted-foreground">
              Full staging queue across operator exceptions, in-flight work, and reprocessing.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canDelete ? (
              <QueueDeleteDialog
                queueIds={tableData.map((row) => row.id)}
                queueReferences={tableData.map((row) => getQueueItemReference(row))}
                onDeleted={() => {
                  setSearch('');
                  setStateFilter('all');
                  setStageFilter('all');
                }}
              />
            ) : null}
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.xls,.xlsx,.eml,.msg,.txt,.csv,.png,.jpg,.jpeg"
              multiple
              onChange={(event) => {
                const input = event.currentTarget;
                const files = Array.from(input.files ?? []);
                if (files.length === 0) return;
                setUploadMessage(null);
                const uploaded: string[] = [];
                void (async () => {
                  for (const file of files) {
                    await upload.mutateAsync(file);
                    uploaded.push(file.name);
                    setUploadMessage(`Uploaded ${uploaded.length} of ${files.length} files.`);
                  }
                  setUploadMessage(
                    uploaded.length === 1
                      ? `Uploaded ${uploaded[0]}. Created queue item for ${uploaded[0]}.`
                      : `Uploaded ${uploaded.length} files. Created queue items for each file.`,
                  );
                })().catch((uploadError: unknown) => {
                  const prefix =
                    uploaded.length > 0 ? `Uploaded ${uploaded.length} of ${files.length}. ` : '';
                  setUploadMessage(
                    uploadError instanceof Error
                      ? `${prefix}Upload failed: ${uploadError.message}`
                      : `${prefix}Upload failed.`,
                  );
                });
                input.value = '';
              }}
            />
            <Button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={upload.isPending}
            >
              <Upload className="h-4 w-4" />
              Upload files
            </Button>
          </div>
        </div>
      </div>

      {uploadMessage ? (
        <Card>
          <CardContent className="py-3 text-sm text-muted-foreground">{uploadMessage}</CardContent>
        </Card>
      ) : null}

      {!isLoading && data ? (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 py-4">
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Filter by source document, customer, PO, product, delivery date..."
              className="min-w-[20rem] flex-1"
            />
            <Select value={stateFilter} onValueChange={setStateFilter}>
              <SelectTrigger className="w-[15rem]">
                <SelectValue placeholder="All states" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All states</SelectItem>
                {stateOptions.map((state) => (
                  <SelectItem key={state} value={state}>
                    {formatWorkflowStateLabel(state)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-[13rem]">
                <SelectValue placeholder="All stages" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All stages</SelectItem>
                {stageOptions.map((stage) => (
                  <SelectItem key={stage} value={stage}>
                    {stage}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-[15rem]">
                <SelectValue placeholder="All owners" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All owners</SelectItem>
                {ownerOptions.hasUnassigned ? (
                  <SelectItem value="__unassigned__">Unassigned</SelectItem>
                ) : null}
                {ownerOptions.emails.map((email) => (
                  <SelectItem key={email} value={email}>
                    {email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 whitespace-nowrap text-sm text-muted-foreground">
              <input
                type="checkbox"
                checked={includeResolved}
                onChange={(event) => setIncludeResolved(event.target.checked)}
                className="h-4 w-4 rounded border"
              />
              Show closed items
            </label>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSearch('');
                setStateFilter('all');
                setStageFilter('all');
                setOwnerFilter('all');
              }}
              disabled={
                search.length === 0 &&
                stateFilter === 'all' &&
                stageFilter === 'all' &&
                ownerFilter === 'all'
              }
            >
              Clear filters
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : null}

      {!isLoading && isError ? (
        <Card>
          <CardContent className="flex items-center justify-between gap-4 py-8">
            <div className="text-sm text-muted-foreground">
              Failed to load the queue. {error instanceof Error ? error.message : 'Unknown error.'}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {!isLoading && data && data.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            {includeResolved
              ? 'Queue is empty.'
              : 'No active items. Check "Show closed items" above to see Done / Closed rows.'}
          </CardContent>
        </Card>
      ) : null}

      {!isLoading && data && data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border bg-card">
          <div>
            <table className="w-full table-fixed text-sm">
              <thead className="border-b bg-muted/40 text-left">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={cn(
                          'px-3 py-3 font-medium text-muted-foreground whitespace-nowrap',
                          getColumnClassName(header.column.id),
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {effectiveQueueViewConfig.groupingEnabled
                  ? queueGroups.map((group) => {
                      const isCollapsed = collapsedGroupKeys.has(group.key);
                      const groupFieldLabel = getQueueGroupingFieldLabel(group.field);
                      return (
                        <Fragment key={group.key}>
                          <tr key={group.key} className="border-b bg-muted/25">
                            <td colSpan={visibleColumnCount} className="px-3 py-2">
                              <button
                                type="button"
                                className="flex w-full items-center gap-2 text-left text-sm"
                                aria-expanded={!isCollapsed}
                                aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${groupFieldLabel} group ${group.label}`}
                                onClick={() => {
                                  setCollapsedGroupKeys((current) => {
                                    const next = new Set(current);
                                    if (next.has(group.key)) next.delete(group.key);
                                    else next.add(group.key);
                                    return next;
                                  });
                                }}
                              >
                                {isCollapsed ? (
                                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                                )}
                                <span className="shrink-0 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                  {groupFieldLabel}
                                </span>
                                <span
                                  className="min-w-0 truncate font-medium text-foreground"
                                  title={group.label}
                                >
                                  {group.label}
                                </span>
                                <span className="shrink-0 text-xs text-muted-foreground">
                                  {group.orderCount} {group.orderCount === 1 ? 'order' : 'orders'}
                                </span>
                                {group.exceptionCount > 0 ? (
                                  <Badge variant="destructive" className="shrink-0">
                                    {group.exceptionCount} exception
                                  </Badge>
                                ) : null}
                                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                                  Newest {formatDateTime(group.newestReceivedOn)}
                                </span>
                              </button>
                            </td>
                          </tr>
                          {isCollapsed
                            ? null
                            : group.rows.map((row) => (
                                <tr
                                  key={row.id}
                                  tabIndex={0}
                                  className={cn(
                                    'cursor-pointer border-b align-middle transition-colors hover:bg-muted/35 focus-visible:bg-muted/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                  )}
                                  onClick={() => navigate(`/queue/${row.original.id}`)}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                      event.preventDefault();
                                      navigate(`/queue/${row.original.id}`);
                                    }
                                  }}
                                >
                                  {row.getVisibleCells().map((cell, cellIndex) => (
                                    <td
                                      key={cell.id}
                                      className={cn(
                                        'min-w-0 px-3 py-3 whitespace-nowrap',
                                        getColumnClassName(cell.column.id),
                                        cellIndex === 0 && 'relative overflow-visible pl-4',
                                      )}
                                    >
                                      {cellIndex === 0 ? <RowStatusStrip row={row.original} /> : null}
                                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                        </Fragment>
                      );
                    })
                  : tableRows.map((row) => (
                      <tr
                        key={row.id}
                        tabIndex={0}
                        className={cn(
                          'cursor-pointer border-b align-middle transition-colors hover:bg-muted/35 focus-visible:bg-muted/35 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        )}
                        onClick={() => navigate(`/queue/${row.original.id}`)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            navigate(`/queue/${row.original.id}`);
                          }
                        }}
                      >
                        {row.getVisibleCells().map((cell, cellIndex) => (
                          <td
                            key={cell.id}
                            className={cn(
                              'min-w-0 px-3 py-3 whitespace-nowrap',
                              getColumnClassName(cell.column.id),
                              cellIndex === 0 && 'relative overflow-visible pl-4',
                            )}
                          >
                            {cellIndex === 0 ? <RowStatusStrip row={row.original} /> : null}
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}
